import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { money, fmtDate, todayStr, PAYMENT_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, RotateCcw, Gift } from "lucide-react";
import { toast } from "sonner";

export default function DailySales() {
  const { user } = useAuth();
  const isMgr = ["owner", "manager"].includes(user?.role);
  const [date, setDate] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/reports/daily", { params: { date } }).then((r) => setReport(r.data)).catch(() => {}), [date]);
  useEffect(() => { load(); }, [load]);

  const refund = (id) => { if (!window.confirm("Refund this sale and restore inventory?")) return; api.post(`/sales/${id}/refund`).then(() => { toast.success("Refunded"); load(); setDetail(null); }).catch((e) => toast.error(formatApiError(e))); };
  const reprint = (id) => api.post(`/sales/${id}/reprint`).then(() => toast.success("Reprint sent")).catch((e) => toast.error(formatApiError(e)));

  if (!report) return <div className="text-slate-400 text-sm">Loading…</div>;
  const tiles = [
    ["Cash", report.cash_sales], ["Credit", report.credit_sales], ["Split", report.split_sales],
    ["Website", report.website_sales], ["DoorDash", report.doordash_sales],
  ];

  return (
    <div className="max-w-6xl space-y-5">
      <div className="flex items-center gap-3">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="daily-date-input" />
        <div className="text-sm text-slate-500">{report.transaction_count} sales · {money(report.total_revenue)} revenue</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {tiles.map(([l, v]) => (
          <Card key={l} className="p-4" data-testid={`daily-tile-${l.toLowerCase()}`}><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-bold text-slate-900">{money(v)}</div></Card>
        ))}
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="daily-tile-prize">
          <div className="text-xs text-amber-700 flex items-center gap-1"><Gift className="w-3 h-3" />PRIZE</div>
          <div className="text-xl font-bold text-amber-800">{report.prize_count}</div>
          <div className="text-[11px] text-amber-600">retail {money(report.prize_retail_value)} · cost {money(report.prize_cost)}</div>
        </Card>
      </div>

      <Card className="divide-y">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-slate-500 uppercase">
          <div className="col-span-2">Receipt</div><div className="col-span-3">Cashier</div><div className="col-span-2">Payment</div><div className="col-span-2 text-right">Total</div><div className="col-span-3 text-right">Actions</div>
        </div>
        {report.sales.map((s) => (
          <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center" data-testid={`sale-row-${s.id}`}>
            <button className="col-span-2 text-left text-indigo-600 hover:underline" onClick={() => setDetail(s)} data-testid={`view-sale-${s.id}`}>#{s.receipt_no}</button>
            <div className="col-span-3 truncate">{s.cashier_name}<div className="text-xs text-slate-400">{fmtDate(s.created_at)}</div></div>
            <div className="col-span-2">
              {s.is_prize ? <Badge className="bg-amber-100 text-amber-700">PRIZE</Badge> : <span>{PAYMENT_LABELS[s.payment_type]}</span>}
              {s.is_refunded && <Badge variant="secondary" className="ml-1">Refunded</Badge>}
            </div>
            <div className="col-span-2 text-right font-semibold">{money(s.total)}</div>
            <div className="col-span-3 flex justify-end gap-1">
              <Button variant="ghost" size="icon" onClick={() => reprint(s.id)} data-testid={`reprint-${s.id}`}><Printer className="w-4 h-4" /></Button>
              {isMgr && !s.is_refunded && <Button variant="ghost" size="icon" onClick={() => refund(s.id)} data-testid={`refund-${s.id}`}><RotateCcw className="w-4 h-4 text-red-500" /></Button>}
            </div>
          </div>
        ))}
        {report.sales.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No sales for this date.</div>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Receipt #{detail?.receipt_no}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {detail.is_prize && <Badge className="bg-amber-100 text-amber-700">PRIZE</Badge>}
              {detail.items.map((it, i) => <div key={i} className="flex justify-between"><span>{it.name} × {it.qty}</span><span>{money(it.unit_price * it.qty)}</span></div>)}
              <div className="border-t pt-2 flex justify-between"><span>Subtotal</span><span>{money(detail.subtotal)}</span></div>
              {detail.discount_amount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount ({detail.discount?.reason})</span><span>-{money(detail.discount_amount)}</span></div>}
              {detail.split_oz_discount > 0 && <div className="flex justify-between text-emerald-600"><span>Split Oz</span><span>-{money(detail.split_oz_discount)}</span></div>}
              <div className="flex justify-between font-bold text-base"><span>Total</span><span>{money(detail.total)}</span></div>
              <div className="text-xs text-slate-400">{PAYMENT_LABELS[detail.payment_type]} · {detail.cashier_name}{detail.customer_name ? ` · ${detail.customer_name}` : ""}</div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => reprint(detail.id)}><Printer className="w-4 h-4 mr-1.5" />Reprint</Button>
                {isMgr && !detail.is_refunded && <Button variant="destructive" className="flex-1" onClick={() => refund(detail.id)}><RotateCcw className="w-4 h-4 mr-1.5" />Refund</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
